import { Injectable, inject } from '@angular/core';
import {
  ShiftTurnover,
  ShiftTurnoverStatus,
  UpdateShiftTurnoverInput,
} from '../../model/shift/shift-turnover';
import { ContactBuilderService } from './contact-builder.service';
import { DirtyStatus } from '../../model/common/dirty-status';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  UntypedFormArray,
  ValidatorFn,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import {
  dynamicSubfieldSort,
  clearFormArray,
} from '../../core/functions/common-functions';
import { isNullOrUndefined } from 'src/app/utils/utils';
import {
  ShiftTurnoverConnection,
  ShiftTurnoverSearch,
} from '../../model/shift/shift-turnover-search-results';
import { getCorrectedDate } from '../../utils/utils';
import { AorBuilderService } from './aor-builder.service';
import {
  ShiftTurnoverMessageKeys,
  ValidationConfig,
} from 'src/app/core/type-helpers/validation-msg';

@Injectable({
  providedIn: 'root',
})
export class ShiftTurnoverBuilderService {
  private _contactBuilder = inject(ContactBuilderService);
  private _aorBuilder = inject(AorBuilderService);

  getStatusLabelForStatus(status: ShiftTurnoverStatus): string {
    if (!status || status === 'N') {
      return 'New';
    } else if (status === 'DC') {
      return 'Departing Complete';
    } else if (status === 'R') {
      return 'Resolved';
    }

    return '';
  }

  buildNullShiftTurnover(groupId: string): ShiftTurnover {
    return {
      id: null,
      shiftGroupId: groupId,
      ShiftDetails: [],
      shiftDate: new Date(),
      status: 'N',
      statusLabel: this.getStatusLabelForStatus('N'),
      createdAt: null,
      businessUnit: null,
    } as unknown as ShiftTurnover;
  }

  buildShiftTurnoverSearchResult(item: any): ShiftTurnoverSearch {
    const firstIncomingFullName =
      item.incomingFirstName + ' ' + item.incomingLastName;
    const secondIncomingFullName =
      item.secondIncomingFirstName + ' ' + item.secondIncomingLastName;
    const retVal: ShiftTurnoverSearch = {
      id: item.id,
      shiftDate: item.shiftDate,
      status: item.status,
      shiftName: item.shiftName,
      shiftTypeId: item.shiftTypeId,
      incomingName: item.secondIncomingFirstName
        ? firstIncomingFullName + ' and ' + secondIncomingFullName
        : firstIncomingFullName,
      departingName: item.departingFirstName + ' ' + item.departingLastName,
      aor: item.aor,
      aorName: item.aorName,
      statusLabel: this.getStatusLabelForStatus(item.status),
    };

    return retVal;
  }

  buildShiftTurnover(item: any): ShiftTurnover {
    const date = new Date(item.shiftDate);
    date.setHours(6);
    date.setMinutes(0);
    date.setSeconds(1);
    const retVal: ShiftTurnover = {
      id: item.id,
      shiftGroupId: item.ShiftGroup ? item.ShiftGroup.id : null,
      shiftDate: date,
      incomingCompletedDate: item.incomingCompletedDate
        ? getCorrectedDate(item.incomingCompletedDate)
        : null,
      departingCompletedDate: item.departingCompletedDate
        ? getCorrectedDate(item.departingCompletedDate)
        : null,
      AOR: item.AOR ? this._aorBuilder.buildAor(item.AOR) : null,
      aor: item.AOR ? item.AOR.name : null,
      ShiftType: item.ShiftType ? item.ShiftType : null,
      shiftTypeId: item.ShiftType ? item.ShiftType.id : null,
      shift: item.ShiftType ? item.ShiftType.name : null,
      businessUnit: item.businessUnit ? item.businessUnit : null,
      status: item.status,
      statusLabel: this.getStatusLabelForStatus(item.status),
      createdAt: getCorrectedDate(item.createdAt)!,
      version:
        item.version !== null && item.version !== undefined ? item.version : -1,
      ShiftDetails: item.ShiftDetails
        ? item.ShiftDetails.map((d: any) => ({
            id: d.id,
            Analyst: this._contactBuilder.buildContact(d.Analyst),
            incomingDepartingFlag: d.incomingDepartingFlag,
            analystId: d.Analyst.id,
            additionalComments: d.additionalComments,
            fitFlag: d.fitFlag,
            scadaFlag: d.scadaFlag,
            reviewedAllSections: d.reviewedAllSections,
            ShiftTurnoverItems: d.ShiftTurnoverItems
              ? d.ShiftTurnoverItems.map((t: any) => ({
                  id: t.id,
                  shiftItemId: t.ShiftItem.id,
                  ShiftItem: {
                    id: t.ShiftItem.id,
                    name: t.ShiftItem.name,
                    sortOrder: t.ShiftItem.sortOrder,
                  },
                  comments: t.comments,
                  turnoverFlag: t.turnoverFlag,
                }))
              : [],
            ShiftTurnoverMitigations: d.ShiftTurnoverMitigations
              ? d.ShiftTurnoverMitigations.map((t: any) => ({
                  id: t.id,
                  shiftMitigationId: t.ShiftMitigation.id,
                  ShiftMitigation: {
                    id: t.ShiftMitigation.id,
                    name: t.ShiftMitigation.name,
                    sortOrder: t.ShiftMitigation.sortOrder,
                  },
                  turnoverFlag: t.turnoverFlag,
                }))
              : [],
          }))
        : [],
    };

    return retVal;
  }

  buildUpdateStatement(
    shiftTurnover: ShiftTurnover,
    input: any
  ): UpdateShiftTurnoverInput {
    if (input.shiftDate) {
      const date = new Date(input.shiftDate);
      date.setHours(6);
      date.setMinutes(0);
      date.setSeconds(1);
      input.shiftDate = date;
    }

    const retVal: any = {
      id: shiftTurnover.id ? shiftTurnover.id : '',
      aorId: input.aor ? input.aor : shiftTurnover.AOR?.id,
      dirtyStatus: shiftTurnover.id ? DirtyStatus.UPDATED : DirtyStatus.NEW,
      shiftDate: input.shiftDate ? input.shiftDate : shiftTurnover.shiftDate,
      shiftGroupId: input.shiftGroupId
        ? input.shiftGroupId
        : shiftTurnover.shiftGroupId,
      status: input.status ? input.status : shiftTurnover.status,
      shiftTypeId: input.shiftType
        ? input.shiftType
        : shiftTurnover.shiftTypeId,
      businessUnit: input.businessUnit ? input.businessUnit : null,
      expectedVersion:
        shiftTurnover.version !== null && shiftTurnover.version !== undefined
          ? shiftTurnover.version + 1
          : -1,
    };

    if (shiftTurnover.ShiftDetails.length > 0) {
      const newDetails = shiftTurnover.ShiftDetails.map((d: any) =>
        this.processDetails(d, shiftTurnover, input)
      );
      retVal['ShiftDetails'] = newDetails.map((d: any) => ({
        id: d.id,
        incomingDepartingFlag: d.incomingDepartingFlag,
        dirtyStatus: DirtyStatus.UPDATED,
        additionalComments: d.additionalComments,
        fitFlag: d.fitFlag,
        scadaFlag: d.scadaFlag,
        ShiftTurnoverItems: d.ShiftTurnoverItems,
        ShiftTurnoverMitigation: d.ShiftTurnoverMitigations,
        analystId: d.analystId,
        reviewedAllSections: d.reviewedAllSections,
      }));
    } else {
      retVal['ShiftDetails'] = [
        {
          id: '',
          analystId: input.incomingAnalyst.id,
          incomingDepartingFlag: 'I',
        },
        {
          id: '',
          analystId: input.departingAnalyst.id,
          incomingDepartingFlag: 'D',
        },
      ];
    }
    return retVal;
  }

  buildCreateStatement(shiftTurnover: any, input: any) {
    if (input.shiftDate) {
      const date = new Date(input.shiftDate);
      date.setHours(6);
      date.setMinutes(0);
      date.setSeconds(1);
      input.shiftDate = date;
    }

    const retVal: any = {
      aorId: input.aor ? input.aor : shiftTurnover.aorId,
      shiftDate: input.shiftDate ? input.shiftDate : shiftTurnover.shiftDate,
      shiftGroupId: input.shiftGroupId
        ? input.shiftGroupId
        : shiftTurnover.shiftGroupId,
      status: input.status ? input.status : shiftTurnover.status,
      shiftTypeId: input.shiftType
        ? input.shiftType
        : shiftTurnover.shiftTypeId,
      businessUnit: input.businessUnit ? input.businessUnit : null,
    };

    if (shiftTurnover.ShiftDetails.length > 0) {
      const newDetails = shiftTurnover.ShiftDetails.map((d: any) =>
        this.processDetails(d, shiftTurnover, input)
      );
      retVal['ShiftDetails'] = newDetails.map((d: any) => ({
        incomingDepartingFlag: d.incomingDepartingFlag,
        additionalComments: d.additionalComments,
        fitFlag: d.fitFlag,
        scadaFlag: d.scadaFlag,
        ShiftTurnoverItems: d.ShiftTurnoverItems,
        ShiftTurnoverMitigation: d.ShiftTurnoverMitigations,
        analystId: d.analystId,
      }));
    } else {
      retVal['ShiftDetails'] = [
        {
          id: '',
          analystId: input.incomingAnalyst.id,
          incomingDepartingFlag: 'I',
        },
        {
          id: '',
          analystId: input.departingAnalyst.id,
          incomingDepartingFlag: 'D',
        },
      ];

      if (input.secondaryIncomingAnalyst) {
        retVal['ShiftDetails'].push({
          id: '',
          analystId: input.secondaryIncomingAnalyst.id,
          incomingDepartingFlag: 'S',
        });
      }
    }
    return retVal;
  }

  private processDetails(shiftDetail: any, shiftTurnover: any, input: any) {
    const update: any[] = [];
    if (shiftDetail.incomingDepartingFlag === 'I') {
      if (input.hasOwnProperty('incomingAdditionalComments')) {
        shiftDetail.additionalComments = input.incomingAdditionalComments;
      }
      if (!isNullOrUndefined(input.incomingFitFlag)) {
        shiftDetail.fitFlag = input.incomingFitFlag;
      }
      if (!isNullOrUndefined(input.incomingScadaFlag)) {
        shiftDetail.scadaFlag = input.incomingScadaFlag;
      }

      shiftDetail.reviewedAllSections =
        input.incomingReviewedAllSections ?? shiftDetail.reviewedAllSections;

      if (input.incomingAnalyst) {
        shiftDetail.analystId = input.incomingAnalyst.id;
      }
    } else if (shiftDetail.incomingDepartingFlag === 'S') {
      if (input.hasOwnProperty('secondIncomingAdditionalComments')) {
        shiftDetail.additionalComments = input.secondIncomingAdditionalComments;
      }
      if (!isNullOrUndefined(input.secondIncomingFitFlag)) {
        shiftDetail.fitFlag = input.secondIncomingFitFlag;
      }
      if (!isNullOrUndefined(input.secondIncomingScadaFlag)) {
        shiftDetail.scadaFlag = input.secondIncomingScadaFlag;
      }

      shiftDetail.reviewedAllSections =
        input.secondIncomingReviewedAllSections ??
        shiftDetail.reviewedAllSections;

      if (input.secondIncomingAnalyst) {
        shiftDetail.analystId = input.secondIncomingAnalyst.id;
      }
    } else if (shiftDetail.incomingDepartingFlag === 'D') {
      let correctDetail = shiftTurnover.ShiftDetails.find(
        (d: any) => d.incomingDepartingFlag === 'D'
      );

      if (!isNullOrUndefined(input.departingFitFlag)) {
        shiftDetail.fitFlag = input.departingFitFlag;
      }
      if (!isNullOrUndefined(input.departingScadaFlag)) {
        shiftDetail.scadaFlag = input.departingScadaFlag;
      }
      if (input.hasOwnProperty('departingAdditionalComments')) {
        shiftDetail.additionalComments = input.departingAdditionalComments;
      }
      if (input.departingAnalyst) {
        shiftDetail.analystId = input.departingAnalyst.id;
      }

      if (input.items) {
        input.items.forEach((i: any) => {
          let correctItem = correctDetail.ShiftTurnoverItems.find(
            (sti: any) => sti.id === i.departingId
          );
          update.push({
            id: i.departingId,
            comments: i?.departingComments ?? correctItem?.comments,
            turnoverFlag: i.departingTurnoverFlag,
            shiftItemId: i.departingShiftItemId,
            shiftDetailsId: shiftDetail.id,
            dirtyStatus: DirtyStatus.UPDATED,
          });
        });
        shiftDetail.ShiftTurnoverItems = [...update];
      }

      const mits: any[] = [];
      if (input.mitigations) {
        input.mitigations.forEach((i: any) => {
          mits.push({
            id: i.id,
            turnoverFlag: i.turnoverFlag,
            shiftMitigationId: i.mitigationId,
            shiftDetailsId: shiftDetail.id,
            dirtyStatus: DirtyStatus.UPDATED,
          });
        });
        shiftDetail.ShiftTurnoverMitigations = [...mits];
      }
    }
    // need to filter down to only fields needed.
    shiftDetail.ShiftTurnoverItems = shiftDetail.ShiftTurnoverItems.map(
      (i: any) => ({
        id: i.id,
        shiftItemId: i.shiftItemId,
        turnoverFlag: i.turnoverFlag,
        dirtyStatus: i.dirtyStatus,
        shiftDetailsId: i.shiftDetailsId,
        comments: i.comments,
      })
    );

    shiftDetail.ShiftTurnoverMitigations =
      shiftDetail.ShiftTurnoverMitigations.map((i: any) => ({
        id: i.id,
        shiftMitigationId: i.shiftMitigationId,
        turnoverFlag: i.turnoverFlag,
        dirtyStatus: i.dirtyStatus,
        shiftDetailsId: i.shiftDetailsId,
      }));
    return shiftDetail;
  }

  commentsTooLongValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const COMMENT_MAX_LENGTH = 750;

      // If no value is provided, no error
      const input = control.value as string;
      if (!input) {
        return null;
      }

      // Check if the input length exceeds the TEXT field limit for latin1 charset
      return input.length > COMMENT_MAX_LENGTH
        ? {
            textTooLong: {
              actualLength: input.length,
              maxLength: COMMENT_MAX_LENGTH,
            },
          }
        : null;
    };
  }

  buildCrmForm(fb: UntypedFormBuilder): UntypedFormGroup {
    return fb.group({
      aor: [null, Validators.required],
      incomingAnalyst: [null, Validators.required],
      departingAnalyst: [null, Validators.required],
      secondaryIncomingAnalyst: [null],
      shiftType: [null, Validators.required],
      shiftDate: [null, Validators.required],
      createdDt: [{ value: null, disabled: true }],
      status: [{ value: null, disabled: true }],
      incomingAdditionalComments: [null, this.commentsTooLongValidator()],
      incomingScadaFlag: [null],
      incomingFitFlag: [null],
      incomingReviewedAllSections: [null],
      secondIncomingAdditionalComments: [null, this.commentsTooLongValidator()],
      secondIncomingScadaFlag: [null],
      secondIncomingFitFlag: [null],
      secondIncomingReviewedAllSections: [null],
      departingAdditionalComments: [null, this.commentsTooLongValidator()],
      departingFitFlag: [null],
      departingScadaFlag: [null],
      items: fb.array([]),
      mitigations: fb.array([]),
      businessUnit: [null, Validators.required],
      departingCompleted: [{ value: null, disabled: true }],
      incomingCompleted: [{ value: null, disabled: true }],
    });
  }

  buildUpdatedCrmForm(
    shiftTurnover: ShiftTurnover,
    form: UntypedFormGroup,
    fb: UntypedFormBuilder
  ) {
    const inArray = shiftTurnover.ShiftDetails.filter(
      (d) => d.incomingDepartingFlag === 'I'
    );
    const depArray = shiftTurnover.ShiftDetails.filter(
      (d) => d.incomingDepartingFlag === 'D'
    );
    const secondaryIncomingArray = shiftTurnover.ShiftDetails.filter(
      (d) => d.incomingDepartingFlag === 'S'
    );
    const statusValue = this.getStatusLabelForStatus(shiftTurnover.status);

    if (shiftTurnover.AOR) {
      form.patchValue({
        aor: shiftTurnover.AOR.id,
      });
    }

    form.patchValue({
      incomingAnalyst:
        inArray.length > 0
          ? {
              id: inArray[0].Analyst?.id,
              fullName:
                inArray[0].Analyst?.firstName +
                ' ' +
                inArray[0].Analyst?.lastName,
            }
          : null,
      departingAnalyst:
        depArray.length > 0
          ? {
              id: depArray[0].Analyst?.id,
              fullName:
                depArray[0].Analyst?.firstName +
                ' ' +
                depArray[0].Analyst?.lastName,
            }
          : null,
      secondaryIncomingAnalyst:
        secondaryIncomingArray.length > 0
          ? {
              id: secondaryIncomingArray[0].Analyst?.id,
              fullName:
                secondaryIncomingArray[0].Analyst?.firstName +
                ' ' +
                secondaryIncomingArray[0].Analyst?.lastName,
            }
          : null,
      shiftType: shiftTurnover.ShiftType ? shiftTurnover.ShiftType.id : null,
      shiftDate: shiftTurnover.shiftDate,
      status: statusValue,
      createdDt: shiftTurnover.createdAt,
      departingCompleted: shiftTurnover.departingCompletedDate,
      incomingCompleted: shiftTurnover.incomingCompletedDate,
      businessUnit: shiftTurnover.businessUnit
        ? shiftTurnover.businessUnit.id
        : null,
    });

    let incoming,
      departing = null;
    if (shiftTurnover.ShiftDetails && shiftTurnover.ShiftDetails.length > 0) {
      incoming = shiftTurnover.ShiftDetails.filter(
        (d) => d.incomingDepartingFlag === 'I'
      )[0];
      departing = shiftTurnover.ShiftDetails.filter(
        (d) => d.incomingDepartingFlag === 'D'
      )[0];
      let secondaryIncoming =
        secondaryIncomingArray.length > 0 ? secondaryIncomingArray[0] : null;
      form.patchValue({
        incomingAdditionalComments: incoming.additionalComments,
        incomingFitFlag: incoming.fitFlag,
        incomingScadaFlag: incoming.scadaFlag,
        incomingReviewedAllSections: incoming.reviewedAllSections,
        departingAdditionalComments: departing.additionalComments,
        departingFitFlag: departing.fitFlag,
        departingScadaFlag: departing.scadaFlag,
        secondIncomingAdditionalComments: secondaryIncoming?.additionalComments,
        secondIncomingFitFlag: secondaryIncoming?.fitFlag,
        secondIncomingScadaFlag: secondaryIncoming?.scadaFlag,
        secondIncomingReviewedAllSections:
          secondaryIncoming?.reviewedAllSections,
      });

      const depItemArray = departing.ShiftTurnoverItems.sort(
        dynamicSubfieldSort('ShiftItem', 'sortOrder', 1)
      );

      const itemsArray = form.get('items') as UntypedFormArray;
      clearFormArray(itemsArray);
      for (let i = 0; i < depItemArray.length; i++) {
        const d = depItemArray[i];

        itemsArray.push(
          fb.group({
            departingId: d.id,
            name: d.ShiftItem?.name,
            departingShiftItemId: d.ShiftItem?.id,
            departingComments: [d.comments, this.commentsTooLongValidator()],
            departingTurnoverFlag: d.turnoverFlag,
          })
        );
      }

      const depMitArray = departing.ShiftTurnoverMitigations.sort(
        dynamicSubfieldSort('ShiftMitigation', 'sortOrder', 1)
      );
      const mitArray = form.get('mitigations') as UntypedFormArray;
      clearFormArray(mitArray);
      for (let j = 0; j < depMitArray.length; j++) {
        const mit = depMitArray[j];
        mitArray.push(
          fb.group({
            id: mit.id,
            mitigationId: mit.ShiftMitigation?.id,
            mitigationName: mit.ShiftMitigation?.name,
            turnoverFlag: mit.turnoverFlag,
          })
        );
      }
    }
  }

  buildValidationMessages(): ValidationConfig<ShiftTurnoverMessageKeys> {
    return {
      messages: {
        departingAdditionalComments: {
          textTooLong: 'CRM.VALIDATION.TEXT_TOO_LONG',
        },
        incomingAdditionalComments: {
          textTooLong: 'CRM.VALIDATION.TEXT_TOO_LONG',
        },
        departingComments: {
          textTooLong: 'CRM.VALIDATION.TEXT_TOO_LONG',
        },
        incomingComments: {
          textTooLong: 'CRM.VALIDATION.TEXT_TOO_LONG',
        },
      },
      params: {
        departingAdditionalComments: {
          textTooLong: {
            field: 'Departing Additional Comments',
          },
        },
        incomingAdditionalComments: {
          textTooLong: {
            field: 'Incoming Additional Comments',
          },
        },
        departingComments: {
          textTooLong: {
            field: 'Departing Comments',
          },
        },
        incomingComments: {
          textTooLong: {
            field: 'Incoming Comments',
          },
        },
      },
    };
  }

  buildShiftTurnoverConnection(item: any): ShiftTurnoverConnection {
    return {
      items: item.items.map((i: any) => this.buildShiftTurnoverSearchResult(i)),
      totalRecords: item.totalRecords,
    };
  }
}
